// src/hooks/useSubscription.js (v3 – cannot get stuck, request-id guarded)

import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import api from "../services/api";
import { API_PATHS } from "../config";

/**
 * Hook to fetch and monitor subscription status.
 *
 * Returns normalized status, metadata, convenience flags, and manual refresh.
 *
 * @param {Object} options
 * @param {boolean} [options.enabled=true] - Fetch on mount if true
 * @param {boolean} [options.debug=false] - Console logs for troubleshooting
 * @returns {Object}
 *   - status: "ACTIVE" | "TRIALING" | "INACTIVE" | "UNKNOWN"
 *   - meta: subscription details from backend
 *   - flags: { isActive, isTrialing, inTrial, isCanceling }
 *   - loading, error
 *   - refresh(): manual refresh function
 */
export default function useSubscription({ enabled = true, debug = false } = {}) {
  const [status, setStatus] = useState("UNKNOWN");
  const [meta, setMeta] = useState({
    stripe_subscription_id: null,
    subscription_cancel_at_period_end: false,
    subscription_cancel_at_iso: null,
    subscription_current_period_end_iso: null,
    trial_ends_at_iso: null,
    trial_days_left: 0,
    trial_hours_left: null,
    trial_seconds_left: null,
  });
  const [loading, setLoading] = useState(Boolean(enabled));
  const [error, setError] = useState(null);

  const reqIdRef = useRef(0);

  const normalizeStatus = (raw) => {
    if (!raw) return "INACTIVE";
    const s = String(raw).toUpperCase().trim();
    if (s === "ACTIVE" || s === "TRIALING" || s === "INACTIVE") return s;
    return "UNKNOWN";
  };

  const refresh = useCallback(async () => {
    if (!enabled) return;

    const myReqId = ++reqIdRef.current;

    setLoading(true);
    setError(null);

    try {
      const url = API_PATHS?.SUBSCRIPTION_STATUS;
      if (!url) throw new Error("API_PATHS.SUBSCRIPTION_STATUS is missing/undefined");

      const { data } = await api.get(url);

      // Ignore stale responses (e.g., if called twice quickly)
      if (myReqId !== reqIdRef.current) return;

      if (debug) console.log("[useSubscription] /subscription-status data:", data);

      setStatus(normalizeStatus(data?.status));

      setMeta({
        stripe_subscription_id: data?.stripe_subscription_id || null,
        subscription_cancel_at_period_end: !!data?.subscription_cancel_at_period_end,
        subscription_cancel_at_iso: data?.subscription_cancel_at_iso || null,
        subscription_current_period_end_iso: data?.subscription_current_period_end_iso || null,
        trial_ends_at_iso: data?.trial_ends_at_iso || null,
        trial_days_left:
          Number.isFinite(Number(data?.trial_days_left)) ? Number(data?.trial_days_left) : 0,
        trial_hours_left:
          Number.isFinite(Number(data?.trial_hours_left)) ? Number(data?.trial_hours_left) : null,
        trial_seconds_left:
          Number.isFinite(Number(data?.trial_seconds_left)) ? Number(data?.trial_seconds_left) : null,
      });
    } catch (e) {
      if (myReqId !== reqIdRef.current) return;

      if (debug) console.warn("[useSubscription] refresh error:", e);

      setError(e);
      // If we fail, do NOT claim inactive — keep UNKNOWN so the UI can show a retry message.
      setStatus("UNKNOWN");
      setMeta({
        stripe_subscription_id: null,
        subscription_cancel_at_period_end: false,
        subscription_cancel_at_iso: null,
        subscription_current_period_end_iso: null,
        trial_ends_at_iso: null,
        trial_days_left: 0,
        trial_hours_left: null,
        trial_seconds_left: null,
      });
    } finally {
      if (myReqId === reqIdRef.current) setLoading(false);
    }
  }, [enabled, debug]);

  useEffect(() => {
    if (enabled) refresh();
  }, [enabled, refresh]);

  const flags = useMemo(() => {
    const isActive = status === "ACTIVE";
    const isTrialing = status === "TRIALING";
    const inTrial = isTrialing && (meta.trial_days_left || 0) > 0;
    const isCanceling = isActive && !!meta.subscription_cancel_at_period_end;
    return { isActive, isTrialing, inTrial, isCanceling };
  }, [status, meta]);

  return { status, meta, flags, loading, error, refresh };
}

/**
 * Helper to format ISO dates for UI
 * @param {string|null|undefined} iso
 * @returns {string|null}
 */
export const formatLocalDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};
